import { useForm } from 'react-hook-form';
import { ApiCustomerIdentity } from '../../../openapi';
import { FormContentIsland, FormTextField, Grid, HookForm, Button } from '@razz-team/ui-web';
import { useCallback, useEffect } from 'react';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';
import {
  useConfirmEmailMutation,
  useSetEmailMutation,
  useUpdateEmailMutation,
} from '../../../api/identity';
import { useTranslation } from 'react-i18next';
import { OtpField } from '../../Field/OtpField';
import { useFormError } from '../../../hooks/useFormError';

type FormValues = {
  email: string;
  otp: string;
};

export function UpdateEmailForm({
  identity,
  onComplete,
}: {
  identity: ApiCustomerIdentity;
  onComplete?: () => void;
}) {
  const { t } = useTranslation();

  const idempotencyKey = useIdempotencyKey();
  const setMutation = useSetEmailMutation({ idempotencyKey });
  const updateMutation = useUpdateEmailMutation({ idempotencyKey });
  const confirmMutation = useConfirmEmailMutation({ idempotencyKey });
  const startMutation = identity.email ? updateMutation : setMutation;
  const activeMutation = startMutation.isSuccess ? confirmMutation : startMutation;

  useEffect(() => {
    if (activeMutation.isSuccess) {
      onComplete?.();
    }
  }, [activeMutation.isSuccess, onComplete]);

  const form = useForm<FormValues>();

  useFormError(form, 'email', activeMutation.error);

  const handleSubmit = useCallback(
    (values: FormValues) => {
      if (startMutation.isSuccess) {
        confirmMutation.mutate({ code: values.otp });
      } else {
        startMutation.mutate({ email: values.email });
      }
    },
    [confirmMutation, startMutation],
  );

  return (
    <Grid
      flexDirection="column"
      gap={4}
      component={HookForm}
      form={form}
      onSubmit={handleSubmit}
      size={12}
    >
      <FormContentIsland>
        <FormTextField
          id="email"
          name="email"
          label={t('Email')}
          fullWidth
          required
          inputMode="email"
          autoComplete="email"
          disabled={startMutation.isSuccess}
          autoFocus={!startMutation.isSuccess}
        />
        {startMutation.isSuccess ? (
          <OtpField name="otp" label={t('OTP code')} fullWidth required autoFocus />
        ) : null}
      </FormContentIsland>
      <Button type="submit" size="large" loading={activeMutation.isPending} fullWidth>
        {t('Submit')}
      </Button>
    </Grid>
  );
}
